import { ChangeEvent, useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import {
  ClickOutsideDetector,
  IconClose,
  IconDown,
  IconSelected,
  IconUp,
  InputSize,
  InputText,
} from '@ui/src';
import { SourceOfIncomeTypes } from './types';
import { CustomerStatus } from '../../../../hoc/customers/types';
import RegulatedCompanyRow from '../../../corporate/aml/parts/row';

import cn from '../aml.module.css';

const SourceOfIncomeItem = ({
  sourcesOfIncome,
  sourceOfIncomeFiltered,
  sourceOfIncome,
  sourceOfIncomeValues,
  setValue,
  baseCurrency,
  control,
  errors,
  clearErrors,
  status,
}: SourceOfIncomeTypes) => {
  const [formValues, setFormValues] = useState<{
    id?: string;
    sourceId: string;
    value: number | string;
  }>();
  const [listVisible, setListVisible] = useState(false);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const nextFormValues = { ...formValues };

    const nextSourceOfIncomeValues = sourceOfIncomeValues?.map((item) => {
      const result = { ...item };

      if (result.id === sourceOfIncome?.id) {
        if (e.target.name === `sourceId-${result.id}`) {
          result.sourceId = e.target.value;
          nextFormValues.sourceId = e.target.value;
        } else if (e.target.name === `value-${result.id}`) {
          result.value = e.target.value;
          nextFormValues.value = e.target.value;
        }
      }

      return result;
    });

    setFormValues(
      nextFormValues as {
        id?: string;
        sourceId: string;
        value: number | string;
      },
    );
    setValue(
      'sourcesOfIncomes',
      nextSourceOfIncomeValues as {
        sourceId: string;
        value: number | string;
      }[],
    );
  };

  const onDelete = () => {
    setValue(
      'sourcesOfIncomes',
      sourceOfIncomeValues?.filter(
        (item) =>
          item?.sourceId !== sourceOfIncome?.sourceId &&
          item?.id !== sourceOfIncome?.id,
      ),
    );
  };

  useEffect(() => {
    if (sourceOfIncome) {
      setFormValues(sourceOfIncome);
    }
  }, [sourceOfIncome]);

  return (
    <div className={cn.sourceOfIncomeItem}>
      <div className={cn.sourceOfIncomeItemTitle}>
        <ClickOutsideDetector setOptionsListVisible={setListVisible}>
          <>
            <button
              type="button"
              className={cn.sourceOfIncomeButton}
              onClick={() => setListVisible(!listVisible)}
              disabled={status === CustomerStatus.CLOSED}
            >
              <span>
                {formValues?.sourceId
                  ? sourcesOfIncome?.find(
                      (item) => item.id === formValues?.sourceId,
                    )?.value
                  : 'Select option'}
              </span>
              {status !== CustomerStatus.CLOSED && (
                <div className={cn.iconWrapper}>
                  {listVisible ? (
                    <IconUp width={13} height={13} />
                  ) : (
                    <IconDown width={13} height={13} />
                  )}
                </div>
              )}
            </button>
            {listVisible && (
              <div className={cn.sourcesOfIncomeValuesList}>
                {formValues?.sourceId && (
                  <button
                    type="button"
                    className={`${cn.sourcesOfIncomeValuesButton} ${cn.active}`}
                  >
                    {
                      sourcesOfIncome?.find(
                        (item) => item.id === formValues?.sourceId,
                      )?.value
                    }
                    <IconSelected />
                  </button>
                )}
                {sourceOfIncomeFiltered
                  ?.filter((item) => item.id !== formValues?.sourceId)
                  ?.map((item) => (
                    <button
                      type="button"
                      className={cn.sourcesOfIncomeValuesButton}
                      key={item.id}
                      onClick={() =>
                        onChange({
                          target: {
                            name: `sourceId-${sourceOfIncome?.id}`,
                            value: item.id,
                          },
                        } as ChangeEvent<HTMLInputElement>)
                      }
                    >
                      {item.value}
                    </button>
                  ))}
              </div>
            )}
          </>
        </ClickOutsideDetector>
      </div>
      <div className={cn.sourceOfIncomeValue}>
        <InputText
          name={`value-${sourceOfIncome?.id}`}
          placeholder="Enter annual income..."
          value={formValues?.value}
          size={InputSize.SMALL}
          className="borderless"
          onChange={(e) =>
            onChange({
              target: {
                name: `value-${sourceOfIncome?.id}`,
                value: e.target.value,
              },
            } as ChangeEvent<HTMLInputElement>)
          }
          currency={baseCurrency}
          disabled={status === CustomerStatus.CLOSED}
          amountInput
        />
        {(sourceOfIncomeValues?.length as number) > 1 &&
          status !== CustomerStatus.CLOSED && (
            <button
              type="button"
              className={cn.removeSourceOfIncomeButton}
              onClick={onDelete}
            >
              <IconClose width={16} height={16} />
            </button>
          )}
      </div>
      {formValues?.sourceId === '971c3f8c-847b-4a8d-9ddc-f311ebf55152' && (
        <div className={cn.additionalSourceOfIncomeRow}>
          <RegulatedCompanyRow title="Specify other source" withoutPadding>
            <Controller
              control={control}
              name="sourcesOfIncomesOther"
              defaultValue=""
              render={({ field: { onChange: onChangeValue, value, name } }) => (
                <InputText
                  name={name}
                  placeholder="Describe details..."
                  value={value}
                  size={InputSize.SMALL}
                  onChange={(e) => {
                    onChangeValue(e);
                    clearErrors(['sourcesOfIncomesOther']);
                  }}
                  className="borderless"
                  disabled={status === CustomerStatus.CLOSED}
                  tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                  error={errors?.sourcesOfIncomesOther?.message}
                />
              )}
            />
          </RegulatedCompanyRow>
        </div>
      )}
      {formValues?.sourceId === 'fe87dd03-1bee-483b-82f2-d0c2df0e3ed6' && (
        <div className={cn.additionalSourceOfIncomeRow}>
          <RegulatedCompanyRow title="Specify other source" withoutPadding>
            <Controller
              control={control}
              name="sourcesOfIncomesBorrowedOther"
              defaultValue=""
              render={({ field: { onChange: onChangeValue, value, name } }) => (
                <InputText
                  name={name}
                  placeholder="Describe details..."
                  value={value}
                  size={InputSize.SMALL}
                  onChange={(e) => {
                    onChangeValue(e);
                    clearErrors(['sourcesOfIncomesBorrowedOther']);
                  }}
                  className="borderless"
                  disabled={status === CustomerStatus.CLOSED}
                  tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                  error={errors?.sourcesOfIncomesBorrowedOther?.message}
                />
              )}
            />
          </RegulatedCompanyRow>
        </div>
      )}
    </div>
  );
};

export default SourceOfIncomeItem;
